import {
  DATE_FILTERS,
  FEE_STATE_LABEL,
  SOLD_STAGES,
  STAGE_LABEL,
  TIER_LABEL,
  dealSummary,
  formatCents,
  formatMoney,
  marketingReport,
  toCents,
  type DateFilter,
  type Job,
  type MarketingContact,
  type MarketingJobRow,
  type MarketingPartner,
  type MarketingPayment,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote, Figure, Modal, Scoreboard } from "../../components/ui";
import { fmtDate, toLocalDateInput } from "../../lib/format";
import { navigate, useAsync } from "../../lib/hooks";
import {
  deleteMarketingPayment,
  fetchLadderInputs,
  fetchMarketingContacts,
  fetchMarketingPartners,
  fetchMarketingPayments,
  recordMarketingPayment,
  setMarketingNoContact,
} from "../../lib/queries";
import { useSession } from "../../session";

/**
 * What the advertising earned, and what we owe for it.
 *
 * Dot Edison is paid a share of the gross profit under a Sales Enablement
 * Agreement with two rates — one for a customer who was not a relationship
 * before, a lower one for new business from a customer we already knew (ADR
 * 0074, ADR 0075). This is the screen both sides read: the jobs the agreement
 * covers, what each one made, which rate it earns, what has been paid and what
 * is still outstanding.
 *
 * The arrangement's `attribution` column picks which jobs count. Dot Edison
 * has been on `lead_source` since 2026-10-27 (ADR 0320): only jobs carrying
 * their lead source, at every stage. On the wide reading (`all_customers`,
 * ADR 0075) every customer in Roof Board who is not tagged No Contact counts,
 * so the scoreboard shows both totals, the gap between them being what the
 * wording costs, and the table is cut to sold jobs; both happen only on that
 * reading. On either one every row can be tagged out of the deal from here,
 * because the place somebody realises a customer should never have counted
 * is while looking at the bill.
 *
 * Nothing here adds anything up. `marketingReport` in packages/core does every
 * figure on the page, off `computeLadder` itself, so this screen and the
 * estimate card can never disagree about what a job made (CLAUDE.md rules 3
 * and 5).
 *
 * The ladder is run in full rather than the four-sum shortcut the list screens
 * use, because a fee is taken of profit and profit needs the cost side too.
 * That is affordable here and nowhere else: it is one advertiser's jobs, not
 * the company's.
 */
export function MarketingReport({ jobs }: { jobs: readonly Job[] }) {
  const { isAdmin } = useSession();
  const [filter, setFilter] = useState<DateFilter>("all");
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [payFor, setPayFor] = useState<MarketingJobRow | null>(null);

  const partners = useAsync(fetchMarketingPartners, []);
  const payments = useAsync(fetchMarketingPayments, []);
  const contactRows = useAsync(fetchMarketingContacts, []);

  const contacts = useMemo(
    () =>
      new Map<string, MarketingContact>(
        (contactRows.data ?? []).map((c) => [c.id, c]),
      ),
    [contactRows.data],
  );

  const partner: MarketingPartner | null = useMemo(() => {
    const list = partners.data ?? [];
    return list.find((p) => p.id === partnerId) ?? list[0] ?? null;
  }, [partners.data, partnerId]);

  /*
   * Whether this arrangement is paid on their own leads or on everything.
   */
  const wide = (partner?.attribution ?? "lead_source") === "all_customers";

  /*
   * The jobs worth running the ladder on. Worked out before the ladders are
   * fetched, because the ladder is the expensive part — `marketingReport`
   * applies the real rules again when it builds the rows and is the authority;
   * this is only what to go and read.
   *
   * On the narrow reading that is their own leads, a handful, all of them.
   * On the wide reading it would be every job the company has ever taken, so
   * it is cut to the ones that sold: a fee is only ever owed or pending on a
   * sold job, and an unpriced lead makes nothing either way. Nothing that
   * carries money is skipped by this.
   */
  const theirJobIds = useMemo(() => {
    if (!partner) return [] as string[];
    if (wide) {
      return jobs
        .filter((j) => !j.is_test && SOLD_STAGES.includes(j.stage))
        .map((j) => j.id);
    }
    const source = (partner.lead_source ?? partner.name ?? "")
      .trim()
      .toLowerCase();
    if (!source) return [] as string[];
    return jobs
      .filter((j) => (j.lead_source ?? "").trim().toLowerCase() === source)
      .map((j) => j.id);
  }, [jobs, partner, wide]);

  const key = theirJobIds.join(",");
  const ladders = useAsync(
    () => fetchLadderInputs(theirJobIds),
    // Refetch when the set of their jobs changes, not on every render of it.
    [key],
  );

  const report = useMemo(
    () =>
      marketingReport(
        partner,
        jobs,
        ladders.data ?? new Map(),
        payments.data ?? [],
        { filter, contacts },
      ),
    [partner, jobs, ladders.data, payments.data, filter, contacts],
  );

  /*
   * On the wide reading the table is the bill, not the pipeline: every job the
   * company has open would otherwise be a row here owing nothing. On the
   * narrow reading their leads are worth seeing, so they stay.
   */
  const visibleRows = useMemo(
    () =>
      wide
        ? report.rows.filter(
            (r) => r.state === "earned" || r.state === "pending",
          )
        : report.rows,
    [report.rows, wide],
  );

  const error =
    partners.error ?? ladders.error ?? payments.error ?? contactRows.error;
  const loading = !ladders.data && theirJobIds.length > 0;

  async function reload() {
    await payments.reload();
  }

  async function reloadContacts() {
    await contactRows.reload();
  }

  return (
    <div className="list">
      <div className="listhead">
        <h2>{partner ? partner.name : "Marketing"}</h2>
        <div className="lh-right">
          {partner && <span className="count">{dealSummary(partner)}</span>}
          {(partners.data?.length ?? 0) > 1 && (
            <select
              className="datefilter"
              aria-label="Marketing partner"
              value={partner?.id ?? ""}
              onChange={(e) => setPartnerId(e.target.value)}
            >
              {(partners.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          <select
            className="datefilter"
            aria-label="Date range"
            value={filter}
            onChange={(e) => setFilter(e.target.value as DateFilter)}
          >
            {DATE_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <ErrorNote error={error} />

      {!partner ? (
        <div className="block">
          <div className="tnone">
            No marketing arrangement is set up yet. One is added in the
            database, not here.
          </div>
        </div>
      ) : (
        <>
          {!report.started && (
            <div className="dupwarn">
              <b>This agreement has not started.</b> No start date is recorded,
              so nothing below is owed — every figure is what the deal would
              come to, shown so it can be read before it is signed. Set the
              start date on the arrangement when it is.
            </div>
          )}

          {partner.agreement_note && (
            <div className="block">
              <h2>The deal</h2>
              <div className="na-hint">{partner.agreement_note}</div>
            </div>
          )}

          <Scoreboard>
            <Figure
              label="Jobs"
              value={String(report.totals.leads)}
              note={wide ? "every customer that counts" : "from their ads"}
            />
            <Figure label="Sold" value={String(report.totals.won)} />
            <Figure
              label="Closed"
              value={String(report.totals.closed)}
              note={`${report.totals.newConnections} new · ${report.totals.existingConnections} repeat`}
            />
            <Figure
              label="Gross profit"
              value={formatMoney(report.totals.profit)}
              note="on the closed jobs"
            />
            <Figure
              label={report.started ? "Earned" : "Would earn"}
              value={formatMoney(report.totals.earned)}
              note={dealSummary(partner)}
            />
            {wide && (
              <Figure
                label="From their ads"
                value={formatMoney(report.totals.sourceFees)}
                note="the rest is the wording"
              />
            )}
            <Figure label="Paid" value={formatMoney(report.totals.paid)} />
            <Figure
              label="Owed now"
              value={formatMoney(report.totals.balance)}
              tone={report.totals.balance > 0 ? "bad" : "good"}
              flag={report.totals.balance > 0 ? "open" : "clear"}
            />
            <Figure
              label="Coming"
              value={formatMoney(report.totals.pending)}
              note={report.started ? "sold, not closed yet" : "not owed"}
            />
            {report.totals.excluded > 0 && (
              <Figure
                label="No Contact"
                value={String(report.totals.excluded)}
                note="held out of the deal"
              />
            )}
          </Scoreboard>

          <div className="block">
            <h2>
              {wide ? "Sold jobs" : "Jobs"}{" "}
              <span className="na-hint">({visibleRows.length})</span>
            </h2>
            {loading ? (
              <div className="tnone">Working out what each job made…</div>
            ) : visibleRows.length === 0 ? (
              <div className="tnone">
                Nothing {partner.name} earns on in this window.
              </div>
            ) : (
              <div className="ewrap">
                <table className="etable" aria-label="Jobs from this partner">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Customer</th>
                      <th>Connection</th>
                      <th>Stage</th>
                      <th>Started</th>
                      <th className="num">Contract</th>
                      <th className="num">Cost</th>
                      <th className="num">Gross profit</th>
                      <th className="num">Rate</th>
                      <th className="num">Their share</th>
                      <th className="num">Paid</th>
                      <th className="num">Owed</th>
                      {isAdmin && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => (
                      <tr key={row.jobId}>
                        <td>
                          <a
                            href={`#job=${row.jobId}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`job=${row.jobId}`);
                            }}
                          >
                            {row.jobNumber}
                          </a>
                        </td>
                        <td>
                          {row.customer || "—"}
                          {row.fromTheirSource && wide && (
                            <span className="na-hint"> · their ad</span>
                          )}
                        </td>
                        <td>
                          {TIER_LABEL[row.tier]}
                          {row.tier === "excluded" && (
                            <span className="na-hint"> · earns nothing</span>
                          )}
                        </td>
                        <td>
                          {STAGE_LABEL[row.stage]}
                          {row.state !== "earned" && (
                            <span className="na-hint">
                              {" "}
                              · {FEE_STATE_LABEL[row.state]}
                            </span>
                          )}
                        </td>
                        <td>{fmtDate(row.createdAt)}</td>
                        <td className="num">{formatMoney(row.contract)}</td>
                        <td className="num">{formatMoney(row.cost)}</td>
                        <td className="num">{formatMoney(row.profit)}</td>
                        <td className="num">
                          {row.ratePct > 0 ? `${row.ratePct}%` : "—"}
                        </td>
                        <td className="num">
                          {formatMoney(row.fee)}
                          {row.state === "pending" && (
                            <span className="na-hint"> · not owed yet</span>
                          )}
                        </td>
                        <td className="num">
                          {row.paid > 0 ? formatMoney(row.paid) : "—"}
                        </td>
                        <td className="num">
                          {row.state === "earned" ? (
                            <b>{formatMoney(row.balance)}</b>
                          ) : (
                            "—"
                          )}
                        </td>
                        {isAdmin && (
                          <td className="mktactions">
                            {row.state === "earned" && row.balance > 0 && (
                              <button
                                className="addrow ghost"
                                onClick={() => setPayFor(row)}
                              >
                                Record payment
                              </button>
                            )}
                            <NoContactButton
                              row={row}
                              jobs={jobs}
                              excluded={row.tier === "excluded"}
                              onChange={reloadContacts}
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5}>
                        <b>Total</b>
                      </td>
                      <td className="num">
                        {formatMoney(report.totals.contract)}
                      </td>
                      <td />
                      <td className="num">
                        {formatMoney(report.totals.profit)}
                      </td>
                      <td />
                      <td className="num">
                        {formatMoney(report.totals.jobFees)}
                      </td>
                      <td className="num">{formatMoney(report.totals.paid)}</td>
                      <td className="num">
                        <b>{formatMoney(report.totals.balance)}</b>
                      </td>
                      {isAdmin && <td />}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            <div className="na-hint" style={{ marginTop: 10 }}>
              {wide ? (
                <>
                  Under this agreement every customer in Roof Board counts,
                  including the ones the reps find themselves, unless the
                  customer is tagged No Contact — which has to be done before
                  the lead has been worked. A customer&rsquo;s first sale earns
                  the new-connection rate and everything after it earns the
                  repeat rate; a customer who was already here when the
                  agreement started is an existing connection from the start.
                </>
              ) : (
                <>
                  A job counts as theirs when its lead source is {partner.name}{" "}
                  — whether their number rang or somebody tagged it by hand.
                </>
              )}{" "}
              A share is worked out on every sold job, but it is only owed once
              the job is closed and the numbers have stopped moving. Contract
              and gross profit come from the estimate itself, so they match what
              the job screen shows.
            </div>
          </div>

          <PaymentHistory
            partnerId={partner.id}
            payments={payments.data ?? []}
            jobs={jobs}
            canEdit={isAdmin}
            onChange={reload}
          />
        </>
      )}

      {payFor && partner && (
        <PaymentModal
          partner={partner}
          row={payFor}
          onClose={() => setPayFor(null)}
          onSaved={async () => {
            setPayFor(null);
            await reload();
          }}
        />
      )}
    </div>
  );
}

/** A payment being recorded against one job. */
function PaymentModal({
  partner,
  row,
  onClose,
  onSaved,
}: {
  partner: MarketingPartner;
  row: MarketingJobRow;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  // Pre-filled with what is outstanding, because paying the balance is what
  // happens almost every time. It is a text box, not a fixed amount: a part
  // payment is a real thing and the balance is what is left.
  const [amount, setAmount] = useState(formatCents(row.balance));
  const [paidOn, setPaidOn] = useState(
    toLocalDateInput(new Date().toISOString()),
  );
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const value = Number.parseFloat(amount.replace(/[$,\s]/g, ""));
  const valid = Number.isFinite(value) && value > 0 && !!paidOn;

  async function save() {
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      await recordMarketingPayment({
        partnerId: partner.id,
        jobId: row.jobId,
        amount: value,
        paidOn,
        note,
      });
      await onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <Modal
      title={`Pay ${partner.name}`}
      subtitle={`Job ${row.jobNumber}${row.customer ? ` · ${row.customer}` : ""} · ${formatMoney(row.balance)} outstanding`}
      onClose={onClose}
      actions={
        <button
          className="primary"
          disabled={!valid || saving}
          // Fire and forget: save() shows its own errors.
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save payment"}
        </button>
      }
    >
      <ErrorNote error={error} />
      <div className="frow">
        <label>
          Amount
          <input
            value={amount}
            inputMode="decimal"
            aria-label="Amount"
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>
        <label>
          Paid on
          <input
            type="date"
            value={paidOn}
            aria-label="Paid on"
            onChange={(e) => setPaidOn(e.target.value)}
          />
        </label>
      </div>
      <label>
        Note
        <input
          value={note}
          placeholder="Cheque number, transfer reference…"
          aria-label="Note"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
    </Modal>
  );
}

/**
 * Everything paid so far, newest first.
 *
 * The list is the receipt. "Who entered this" is shown for the same reason it
 * is stored: the advertiser signs in here too, and a payment nobody at the
 * company recorded is worth being able to see.
 */
function PaymentHistory({
  partnerId,
  payments,
  jobs,
  canEdit,
  onChange,
}: {
  partnerId: string;
  payments: readonly MarketingPayment[];
  jobs: readonly Job[];
  canEdit: boolean;
  onChange: () => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const jobNumber = useMemo(
    () => new Map(jobs.map((j) => [j.id, j.job_number])),
    [jobs],
  );
  const mine = payments.filter((p) => p.partner_id === partnerId);

  async function remove(id: string) {
    setError(null);
    try {
      await deleteMarketingPayment(id);
      await onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="block">
      <h2>
        Payments <span className="na-hint">({mine.length})</span>
      </h2>
      <ErrorNote error={error} />
      {mine.length === 0 ? (
        <div className="tnone">Nothing paid yet.</div>
      ) : (
        <div className="ewrap">
          <table className="etable" aria-label="Payments">
            <thead>
              <tr>
                <th>Paid on</th>
                <th>Job</th>
                <th className="num">Amount</th>
                <th>Note</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {mine.map((payment) => (
                <tr key={payment.id}>
                  <td>{fmtDate(payment.paid_on)}</td>
                  <td>{jobNumber.get(payment.job_id) ?? "—"}</td>
                  <td className="num">
                    {formatMoney(toCents(payment.amount))}
                  </td>
                  <td>{payment.note ?? ""}</td>
                  {canEdit && (
                    <td>
                      <button
                        className="addrow ghost"
                        // Fire and forget: remove() shows its own errors.
                        onClick={() => void remove(payment.id)}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Hold a customer out of the arrangement, or put them back in.
 *
 * On the row rather than on the customer screen because this is where somebody
 * realises it: reading a bill and finding a name their own rep door-knocked.
 * It writes to the customer, so it applies to every job that customer ever has
 * — which is what the agreement's tag means.
 *
 * The confirmation is not ceremony. Under the wide reading this is the only
 * thing standing between a customer and a percentage, and the agreement wants
 * the tag on before the lead has been worked, so a tag applied today on a job
 * from last year is an argument waiting to happen. Saying so first is cheaper
 * than having it.
 */
function NoContactButton({
  row,
  jobs,
  excluded,
  onChange,
}: {
  row: MarketingJobRow;
  jobs: readonly Job[];
  excluded: boolean;
  onChange: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contactId = jobs.find((j) => j.id === row.jobId)?.contact_id ?? null;
  if (!contactId) return null;

  async function toggle() {
    const who = row.customer || `job ${row.jobNumber}`;
    const question = excluded
      ? `Put ${who} back into the marketing arrangement? Their jobs will earn a percentage again.`
      : `Tag ${who} as No Contact? They will earn the marketing company nothing, on this job and every other one they ever have.`;
    if (!window.confirm(question)) return;
    setBusy(true);
    setError(null);
    try {
      await setMarketingNoContact(contactId!, !excluded);
      await onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        className="addrow ghost"
        disabled={busy}
        // Fire and forget: toggle() shows its own errors.
        onClick={() => void toggle()}
      >
        {excluded ? "Put back in" : "No Contact"}
      </button>
      {error && <span className="na-hint"> {error}</span>}
    </>
  );
}
